/**
 * /api/hotels の外部入力を正規化する純粋関数群（KV・ネットワーク非依存＝ユニットテスト可能）。
 *
 * 目的:
 *  1) 上流（楽天トラベルAPI）呼び出しの増幅を防ぐ ── radius/日付を有限な現実的値に丸める。
 *  2) KVキャッシュのキー空間を有限化する ── `9999-99-99` のような正規表現だけ通る値を弾く。
 *
 * ⚠️ maxTiles / geo は「外部パラメータとして受け取らない」ことで増幅を断つ（本モジュールでは扱わない）。
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** UIの検索半径ボタンと一致（src/app.ts radiusButtons）。 */
export const ALLOWED_RADII = [20, 40, 60] as const;
/** チェックイン〜アウトの最大泊数。 */
export const MAX_STAY_NIGHTS = 30;
/** 今日から何日先までを予約対象の窓とみなすか。 */
export const MAX_LEAD_DAYS = 365;
const DAY_MS = 86_400_000;

/** UIが送る {20,40,60} 以外は既定20へ。→ 半径のキー空間を3値に固定。 */
export function normalizeRadius(raw: string | null): number {
  const n = Number(raw);
  return n === 40 || n === 60 ? n : 20;
}

/**
 * 宿泊人数・部屋数の上限（UIの選択肢と一致させる。src/lib/trip.ts が同じ値を使う）。
 * 楽天 VacantHotelSearch の adultNum / roomNum は仕様上 1〜10 だが、
 * KVキー空間と上流呼び出しの増幅を抑えるため、現実的な範囲に絞る。
 */
export const MAX_ADULTS = 8;
export const MAX_ROOMS = 4;

/**
 * 大人人数・部屋数の正規化。整数でない/範囲外/未指定は既定（大人2名・1室）へ。
 *
 * ★既定を2名にしている理由: 楽天APIの adultNum 既定は1で、1名検索は「1名で泊まれるプラン」しか
 *   ヒットせず、和室・ペンション等が落ちる。観覧旅行の実態（2名以上）と合わないので、
 *   UIの既定と同じ2名をサーバ側の既定にもする（UIが値を送らない旧クライアントでも同じ結果になる）。
 */
export function normalizeGuests(
  adultsRaw: string | null,
  roomsRaw: string | null,
): { adultNum: number; roomNum: number } {
  const a = Math.round(Number(adultsRaw));
  const r = Math.round(Number(roomsRaw));
  const adultNum = Number.isFinite(a) && a >= 1 ? Math.min(MAX_ADULTS, a) : 2;
  const roomNum = Number.isFinite(r) && r >= 1 ? Math.min(MAX_ROOMS, r) : 1;
  // 1室あたり0人の検索は成立しない（部屋数が人数を超えないように丸める）
  return { adultNum, roomNum: Math.min(roomNum, adultNum) };
}

/**
 * 実在するカレンダー日付なら UTC Date を返す。正規表現は通るが実在しない日付（例 2026-13-40 / 9999-99-99）は null。
 */
export function parseCalendarDate(s: string | null): Date | null {
  if (!s || !DATE_RE.test(s)) return null;
  const parts = s.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * DAY_MS);
}
function startOfUtcDay(ms: number): Date {
  const n = new Date(ms);
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
}

/**
 * checkin/checkout を「今日〜今日+365日」かつ checkout∈[checkin+1, checkin+30泊] に丸める。
 * 範囲外・不正は既定（今日 / 翌日）へフォールバック。→ 日付のキー空間を有限化しつつUIは壊さない。
 */
export function normalizeStayDates(
  checkinRaw: string | null,
  checkoutRaw: string | null,
  nowMs: number,
): { checkinDate: string; checkoutDate: string } {
  const today = startOfUtcDay(nowMs);
  const maxCheckin = addDays(today, MAX_LEAD_DAYS);

  let checkin = parseCalendarDate(checkinRaw);
  if (!checkin || checkin < today || checkin > maxCheckin) checkin = today;

  const minCheckout = addDays(checkin, 1);
  const maxCheckout = addDays(checkin, MAX_STAY_NIGHTS);
  let checkout = parseCalendarDate(checkoutRaw);
  if (!checkout || checkout < minCheckout || checkout > maxCheckout) checkout = minCheckout;

  return { checkinDate: iso(checkin), checkoutDate: iso(checkout) };
}
